import { callableLinesKey, myPhoneLinesKey } from '../data/phone';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  applyCanvasOrder,
  refreshSharedCanvases,
  removeCanvas,
  upsertCanvas,
} from '../data/canvases';
import { upsertList, removeList } from '../data/lists';
import { applyReadReceipt } from '../data/reads';
import { useQueryClient } from '@tanstack/react-query';
import {
  isQuietMessage,
  type Notification,
  type Presence,
  type PublicUser,
  type ServerFrame,
} from '@avslack/shared';
import type { SessionInfo } from '../lib/api';
import { buildWebSocketUrl, RealtimeClient, type ConnectionStatus } from './client';
import {
  applyMemberCount,
  applyReadUpdate,
  bumpChannel,
  removeChannel,
  upsertChannel,
} from '../data/channels';
import {
  applyIncomingMessage,
  applyMessageDeleted,
  applyMessageUpdate,
  applyImportantChange,
  applyTaskChange,
  applyPinChange,
  applyReactionChange,
  applySavedChange,
  applyThreadUpdate,
  resyncAllChannels,
} from '../data/messages';
import { usersKey } from '../data/users';
import { removeHuddle, upsertHuddle } from '../data/huddles';
import {
  emitHuddleCancelled,
  emitHuddleEvicted,
  emitHuddleInvited,
  emitHuddleUpdated,
} from '../huddle/bus';
import { pushEphemeral } from '../data/ephemeral';
import { emitReminderDue } from '../lib/reminder-bus';
import { emitAlertFired, emitAlertResolved } from '../lib/alert-bus';
import { calendarKey, emitCalendarReminder } from '../data/calendar';

interface TypingEntry {
  userId: string;
  expiresAt: number;
}

interface RealtimeContextValue {
  status: ConnectionStatus;
  /** Csatornankent azok, akik eppen irnak (a sajat magunk nelkul). */
  typingByChannel: Record<string, TypingEntry[]>;
  notifyTyping: (channelId: string) => void;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/** Ennel surubben nem kuldunk gepel-jelzest, barmennyit is gepel a felhasznalo. */
const TYPING_THROTTLE_MS = 3000;

export function RealtimeProvider({
  children,
  meId,
  onNotification,
}: {
  children: ReactNode;
  meId: string;
  /** A szerver dontott: ez az uzenet ertesitest erdemel a szamodra. */
  onNotification?: (notification: Notification) => void;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [typingByChannel, setTypingByChannel] = useState<Record<string, TypingEntry[]>>({});
  const clientRef = useRef<RealtimeClient | null>(null);
  const lastTypingSentAt = useRef<Record<string, number>>({});
  const notifyRef = useRef(onNotification);
  notifyRef.current = onNotification;

  useEffect(() => {
    const client = new RealtimeClient(buildWebSocketUrl);
    clientRef.current = client;
    let hasConnectedOnce = false;

    const offFrame = client.onFrame((frame: ServerFrame) => {
      switch (frame.type) {
        case 'message.created': {
          void applyIncomingMessage(queryClient, frame.message);
          // Uj feladat: a fejlec "Feladatok" doboza is frissuljon.
          if (frame.message.task) {
            void queryClient.invalidateQueries({ queryKey: ['tasks', frame.message.channelId] });
          }
          bumpChannel(
            queryClient,
            frame.message.channelId,
            frame.message.seq,
            frame.message.createdAt,
            isQuietMessage(frame.message),
          );
          // A sajat uzenetunk nem olvasatlan - a szerver is ezt irja vissza,
          // de addig se villanjon fel a jelveny.
          if (frame.message.userId === meId) {
            applyReadUpdate(queryClient, frame.message.channelId, frame.message.seq);
          }
          break;
        }

        // Ezek meglevo uzenetet modositanak azonosito alapjan - nincs sorszamuk,
        // ezert nem is kell hozzajuk hezag-ellenorzes.
        case 'message.updated':
          applyMessageUpdate(queryClient, frame.message);
          break;

        case 'message.deleted':
          applyMessageDeleted(queryClient, frame.channelId, frame.messageId);
          // Ha feladat volt, a fejlec dobozabol is tunjon el.
          void queryClient.invalidateQueries({ queryKey: ['tasks', frame.channelId] });
          break;

        case 'reaction.changed':
          applyReactionChange(queryClient, frame.channelId, frame.messageId, frame.reactions);
          break;

        case 'notification': {
          notifyRef.current?.(frame.notification);
          // Az olvasatlan-szamokat az uzenet-esemeny mar frissitette; itt csak
          // a csatornalistat kerjuk ujra, hogy az emlites-jelzes is stimmeljen.
          void queryClient.invalidateQueries({ queryKey: ['channels'] });
          break;
        }

        case 'pin.changed':
          applyPinChange(queryClient, frame.channelId, frame.messageId, frame.isPinned);
          break;

        case 'important.changed':
          applyImportantChange(queryClient, frame.channelId, frame.messageId, frame.isImportant);
          break;

        case 'task.changed':
          applyTaskChange(queryClient, frame.channelId, frame.messageId, frame.task);
          break;

        // Szavazat erkezett vagy lezartak (U4): a kartya ujrakerdez.
        case 'poll.updated':
          void queryClient.invalidateQueries({ queryKey: ['poll', frame.pollId] });
          break;

        case 'saved.changed':
          applySavedChange(queryClient, frame.channelId, frame.messageId, frame.isSaved);
          break;

        case 'thread.updated':
          applyThreadUpdate(
            queryClient,
            frame.channelId,
            frame.parentId,
            frame.replyCount,
            frame.lastReplyAt,
          );
          break;

        case 'canvas.changed':
          upsertCanvas(queryClient, frame.canvas);
          break;

        case 'canvas.removed':
          removeCanvas(queryClient, frame.channelId, frame.canvasId);
          break;

        case 'canvas.reordered':
          applyCanvasOrder(queryClient, frame.channelId, frame.canvasIds);
          break;

        case 'canvas.shared':
          refreshSharedCanvases(queryClient);
          break;

        /*
          LISTA valtozott - a teteleivel egyutt.

          A canvasszal ellentetben itt a teljes lista erkezik: egy pipalas az
          osszegzo szamokat is elmozditja, es ha csak jelzest kapnank, minden
          apro valtozas utan ujra le kellene kerdezni.
        */
        case 'list.changed':
          upsertList(queryClient, frame.list);
          break;

        case 'list.removed':
          removeList(queryClient, frame.channelId, frame.listId);
          break;

        /*
          EMLEKEZTETO jart le.

          A nemitast SZANDEKOSAN nem nezzuk: ez a rendszer egyetlen olyan
          ertesitese, ami attori a csendet. Aki emlekeztetot kert, azt eppen
          az erdekli, hogy akkor is eszrevegye, ha csendre allitotta magat.
        */
        // Naptar (H1): valtozott - minden betoltott naptar ujratoltodik.
        case 'calendar.changed':
          void queryClient.invalidateQueries({ queryKey: calendarKey });
          break;

        case 'calendar.reminder':
          emitCalendarReminder({
            eventId: frame.eventId,
            title: frame.title,
            startsAt: frame.startsAt,
            withCall: frame.withCall,
            channelId: frame.channelId,
            scope: frame.scope,
            groupId: frame.groupId,
          });
          break;

        case 'reminder.due':
          emitReminderDue({
            reminderId: frame.reminderId,
            deliveryId: frame.deliveryId,
            text: frame.text,
            creatorId: frame.creatorId,
            remindAt: frame.remindAt,
            ...(frame.link ? { link: frame.link } : {}),
          });
          // Mentett elem emlekeztetoje: a listan mar ne alljon fuggokent (U7).
          if (frame.link) void queryClient.invalidateQueries({ queryKey: ['saved'] });
          break;

        // RIASZTAS - csak a szuperadmin kapja; a nemitast itt sem nezzuk.
        case 'alert.fired':
          emitAlertFired({ alert: frame.alert, grafanaUrl: frame.grafanaUrl });
          break;
        case 'alert.resolved':
          emitAlertResolved(frame.fingerprint);
          break;

        case 'channel.created':
        case 'channel.updated':
          upsertChannel(queryClient, frame.channel);
          // Uj telefonvonal csatornaja is lehet - az oldalsav "Telefon" sora ebbol el.
          if (frame.type === 'channel.created') {
            void queryClient.invalidateQueries({ queryKey: myPhoneLinesKey });
          }
          break;

        case 'channel.removed':
          removeChannel(queryClient, frame.channelId);
          break;

        // Egy vonalat atiranyitottak, felvettek vagy toroltek.
        case 'phone.lines.changed':
          void queryClient.invalidateQueries({ queryKey: myPhoneLinesKey });
          void queryClient.invalidateQueries({ queryKey: callableLinesKey });
          break;

        case 'member.joined':
        case 'member.left':
          applyMemberCount(queryClient, frame.channelId, frame.memberCount);
          break;

        case 'read.receipt':
          applyReadReceipt(queryClient, frame.channelId, frame.userId, frame.lastReadSeq);
          break;

        case 'read.updated':
          applyReadUpdate(queryClient, frame.channelId, frame.lastReadSeq);
          break;

        case 'user.updated': {
          queryClient.setQueryData<{ users: PublicUser[]; presence: Record<string, Presence> }>(
            usersKey,
            (current) =>
              current
                ? {
                    ...current,
                    users: current.users.map((u) => (u.id === frame.user.id ? frame.user : u)),
                  }
                : current,
          );
          // A session-bejegyzes `{ user, mustChangePassword }` alaku: csak a
          // `user` mezot csereljuk, kulonben a tobbi mezo eltunne.
          if (frame.user.id === meId) {
            queryClient.setQueryData<SessionInfo>(['session'], (current) =>
              current ? { ...current, user: frame.user } : current,
            );
          }
          break;
        }

        /**
         * A huddle allapota MINDIG teljes egeszeben erkezik, nem kulonbsegkent.
         * Ezert egy elveszett esemeny nem tud tartos elteres lenni: a kovetkezo
         * frissites helyreallitja, ki van bent.
         */
        case 'huddle.started':
        case 'huddle.updated':
          upsertHuddle(queryClient, frame.huddle);
          /*
            A HIVAS ABLAKA is ebbol frissul.

            Enelkul csak az eletjel (20 mp) hozna at a valtozast: a nemitas
            ikonja kesve jelenne meg, es egy elutasitott hivas percekig ugy
            latszana, mintha meg csorogne.
          */
          emitHuddleUpdated(frame.huddle);
          break;

        case 'huddle.ended':
          removeHuddle(queryClient, frame.huddleId);
          // A meg ki nem vett hivas ilyenkor MEGSZUNT - a csorgest le kell allitani.
          emitHuddleCancelled(frame.huddleId);
          /*
            AZ ATIRAT ALLAPOTAT is ujra meg kell kerdezni.

            A folyamban levo allapot a hivas KEZDETEKOR keszult, amikor meg nem
            volt mit atirni - es az allapot-lekerdezes csak akkor kezd
            figyelni, ha mar lat egy "keszul" erteket. Enelkul tehat sosem
            indult el: a hivas vegen a gomb nem jelent meg, es a felhasznalo
            csak az oldal ujratoltesekor latta meg, hogy van atirat.

            Az `ended` az EGYETLEN pillanat, amikor biztosan tudjuk, hogy
            mostantol van ertelme ranezni.
          */
          void queryClient.invalidateQueries({ queryKey: ['transcript-states'] });
          /*
            ES MEG EGYSZER, par masodperc mulva.

            Az UTOLSO hangdarab feltoltese a hivas vege UTAN fejezodik be: a
            felvetel lezarasa, a feltoltes es a sor-beiras mind ido. Ha csak az
            `ended` pillanataban kerdeznenk, meg nem lenne mire varni ("nincs
            atirat"), es a lekerdezes nem kezdene figyelni - epp azt a
            helyzetet allitanank vissza, amit javitunk.
          */
          setTimeout(
            () => void queryClient.invalidateQueries({ queryKey: ['transcript-states'] }),
            8000,
          );
          break;

        case 'huddle.evicted':
          emitHuddleEvicted(frame.huddleId, frame.reason);
          break;

        /**
         * A behivast VISSZAVONTAK, mielott felvettuk volna: a hivas el, de NALUNK megszunt.
         * Ugyanaz a jelzes, mint a hivas vegenel - a csengetest le kell allitani.
         */
        case 'huddle.invite_cancelled':
          emitHuddleCancelled(frame.huddleId);
          break;

        /** Behivtak - ez az, ami NALUNK csengetni fog. */
        case 'huddle.invited':
          upsertHuddle(queryClient, frame.huddle);
          emitHuddleInvited({
            huddle: frame.huddle,
            invitedByName: frame.invitedByName,
            channelName: frame.channelName,
            pushed: frame.pushed,
          });
          break;

        /** Slash parancs valasza, ami csak nekem szol es nem kerul az elozmenybe. */
        case 'ephemeral':
          pushEphemeral(frame.channelId, frame.source, frame.text);
          break;

        case 'presence.changed': {
          queryClient.setQueryData<{ users: PublicUser[]; presence: Record<string, Presence> }>(
            usersKey,
            (current) =>
              current
                ? { ...current, presence: { ...current.presence, [frame.userId]: frame.presence } }
                : current,
          );
          break;
        }

        case 'typing.started': {
          if (frame.userId === meId) break;
          setTypingByChannel((current) => {
            const entries = (current[frame.channelId] ?? []).filter(
              (e) => e.userId !== frame.userId,
            );
            return {
              ...current,
              [frame.channelId]: [...entries, { userId: frame.userId, expiresAt: frame.expiresAt }],
            };
          });
          break;
        }

        default:
          break;
      }
    });

    const offStatus = client.onStatus((next, previous) => {
      setStatus(next);

      if (next === 'online') {
        // Visszatertunk egy szakadas utan: minden betoltott csatornat potolunk.
        // Enelkul a kapcsolat alatt erkezett uzenetek nemak maradnanak.
        if (hasConnectedOnce) {
          void resyncAllChannels(queryClient);
          void queryClient.invalidateQueries({ queryKey: ['channels'] });
        }
        hasConnectedOnce = true;
      }

      if (next === 'unauthorized' && previous !== 'unauthorized') {
        window.location.href = '/login';
      }
    });

    client.start();

    return () => {
      offFrame();
      offStatus();
      client.stop();
      clientRef.current = null;
    };
  }, [queryClient, meId]);

  // A lejart gepel-jelzesek takaritasa.
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setTypingByChannel((current) => {
        let changed = false;
        const next: Record<string, TypingEntry[]> = {};
        for (const [channelId, entries] of Object.entries(current)) {
          const alive = entries.filter((e) => e.expiresAt > now);
          if (alive.length !== entries.length) changed = true;
          if (alive.length > 0) next[channelId] = alive;
          else if (entries.length > 0) changed = true;
        }
        return changed ? next : current;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const notifyTyping = useCallback((channelId: string) => {
    const now = Date.now();
    if (now - (lastTypingSentAt.current[channelId] ?? 0) < TYPING_THROTTLE_MS) return;
    lastTypingSentAt.current[channelId] = now;
    clientRef.current?.send({ type: 'typing', channelId, parentId: null });
  }, []);

  const value = useMemo(
    () => ({ status, typingByChannel, notifyTyping }),
    [status, typingByChannel, notifyTyping],
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime csak RealtimeProvider-én belül használható');
  return context;
}
